import React, { useEffect, useMemo, useState } from 'react';
import { arrayOf, bool, func, object, shape, string } from 'prop-types';
import { compose } from 'redux';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import Fuse from 'fuse.js';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { useIntl, intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { isMainSearchTypeKeywords } from '../../util/search';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import { Page, LayoutSingleColumn, IconSearch, IconSpinner } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import {
  fetchStrapiHelpPages,
  getHelpPages,
  getHelpPagesInProgress,
} from '../../ducks/strapi.duck';

import { isArrayLength, keepValidGermanChars } from '../../util/genericHelpers';
import css from './SupportListPage.module.css';
import { createSlug } from '../../util/urlHelpers';
import { draftSlug } from '../../routing/routeConfiguration';
import { createResourceLocatorString } from '../../util/routes';

// --- Helpers ---
const extractText = description => {
  if (Array.isArray(description)) {
    return description
      .map(desc => desc.children?.map(child => child.text || '').join(' ') || '')
      .join(' ');
  }
  return '';
};

const truncateText = (text, lines = 3) => {
  const lineHeight = 1.5;
  const maxHeight = lines * lineHeight * 16;
  return (
    <div
      style={{
        overflow: 'hidden',
        display: '-webkit-box',
        WebkitLineClamp: lines,
        WebkitBoxOrient: 'vertical',
        maxHeight: `${maxHeight}px`,
        lineHeight: `${lineHeight}em`,
      }}
    >
      {keepValidGermanChars(text?.replace(/#/g, ' '))}
    </div>
  );
};

// Find matching object based on slug
export const findMatchingObject = (data, slug) => {
  let matchingObject = null;
  Object.keys(data).some(key => {
    const { attributes } = data[key];
    if (attributes.slug === slug) {
      matchingObject = data[key];
      return true;
    }
    return false;
  });
  return matchingObject;
};

// Shared hook to fetch pages
const useHelpPages = () => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(fetchStrapiHelpPages());
  }, [dispatch]);

  const helpPagesContent = useSelector(getHelpPages);
  const searchInProgress = useSelector(getHelpPagesInProgress);

  return { helpPagesContent, searchInProgress };
};

// --- Autocomplete component (mirrors SupportPage’s approach) ---
const AutocompleteSearch = ({
  articles,
  currentSlug,
  routeConfiguration,
  history,
  intl,
  onQueryChange,
  query, // controlled from parent so list filter + suggestions stay in sync
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [fuse, setFuse] = useState(null);

  const allTitles = useMemo(() => {
    // map current list’s articles to a flat array for Fuse
    return (articles || []).map(item => ({
      id: item?.id,
      title: item?.title,
      slug: currentSlug,
    }));
  }, [articles, currentSlug]);

  useEffect(() => {
    if (allTitles.length > 0) {
      const options = {
        keys: ['title'],
        threshold: 0.3,
      };
      setFuse(new Fuse(allTitles, options));
    } else {
      setFuse(null);
    }
  }, [allTitles]);

  const handleSearchChange = e => {
    const q = e.target.value;
    onQueryChange(q);

    if (q.length > 0 && fuse) {
      const filteredSuggestions = fuse.search(q).map(result => result.item);
      setSuggestions(filteredSuggestions);
    } else {
      setSuggestions([]);
    }
  };

  const handleSuggestionClick = suggestion => {
    onQueryChange(''); // clear the input
    setSuggestions([]);

    const params = {
      title: createSlug(suggestion?.title || draftSlug),
      id: String(suggestion?.id),
      slug: suggestion?.slug,
    };

    history.push(
      createResourceLocatorString('SupportDetailPage', routeConfiguration, { ...params }, {})
    );
  };

  return (
    <div className={css.autocompleteContainer}>
      <div className={css.searchForm}>
        <IconSearch />
        <input
          type="text"
          placeholder={intl.formatMessage({ id: 'SupportListPage.searchPlaceholder', defaultMessage: 'Search' })}
          value={query}
          onChange={handleSearchChange}
          className={css.searchInput}
        />
      </div>

      {suggestions.length > 0 && (
        <ul className={css.suggestionsList}>
          {suggestions.map((s, i) => (
            <li
              key={`${s.id}-${i}`}
              className={css.suggestionItem}
              onClick={() => handleSuggestionClick(s)}
            >
              <IconSearch /> {s.title}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

AutocompleteSearch.propTypes = {
  articles: arrayOf(object).isRequired,
  currentSlug: string,
  routeConfiguration: arrayOf(propTypes.route).isRequired,
  history: shape({ push: func.isRequired }).isRequired,
  intl: intlShape.isRequired,
  onQueryChange: func.isRequired,
  query: string.isRequired,
};

// --- Page component ---
const SupportListPageComponent = props => {
  const {
    intl,
    history,
    routeConfiguration,
    scrollingDisabled,
    params: { type },
  } = props;

  const [searchQuery, setSearchQuery] = useState('');

  const { helpPagesContent: supportListPagesContent, searchInProgress } = useHelpPages();

  const currentSupportListPageContent = supportListPagesContent
    ? findMatchingObject(supportListPagesContent, type)
    : null;

  const currentSlug = currentSupportListPageContent?.attributes?.slug;
  const articles = currentSupportListPageContent?.attributes?.content || [];

  // Keep your existing in-list filtering so the grid below narrows as you type
  const filteredArticles = (articles || []).filter(article => {
    const title = article?.title || '';
    const description = extractText(article?.description);
    const q = (searchQuery || '').toLowerCase();

    return (
      (typeof title === 'string' && title.toLowerCase().includes(q)) ||
      (typeof description === 'string' && description.toLowerCase().includes(q))
    );
  });

  const navigateToDetailPage = params => {
    history.push(
      createResourceLocatorString('SupportDetailPage', routeConfiguration, { ...params }, {})
    );
  };

  const currentTag = currentSupportListPageContent?.attributes?.tag?.data?.attributes?.name;

  return (
    <Page scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.pageRoot}>
          <div className={css.introHeader}>
            <span>{currentTag}</span>
            <div className={css.titleBreadcrumbs}>
              <h2
                role="button"
                className={css.breadCrumb}
                onClick={() =>
                  history.push(
                    createResourceLocatorString('SupportPage', routeConfiguration, {}, {})
                  )
                }
              >
                {currentSupportListPageContent?.attributes?.title
                  ? intl.formatMessage({ id: 'SupportListPage.helpTitle' })
                  : ''}
              </h2>
              <h2>{currentSupportListPageContent?.attributes?.title ? '>' : ''}</h2>
              <h2>{currentSupportListPageContent?.attributes?.title}</h2>
            </div>
            <p>{currentSupportListPageContent?.attributes?.shortDescription}</p>

            {/* Autocomplete (Fuse) + controlled input shared with list filter */}
            <AutocompleteSearch
              articles={articles}
              currentSlug={currentSlug}
              routeConfiguration={routeConfiguration}
              history={history}
              intl={intl}
              query={searchQuery}
              onQueryChange={setSearchQuery}
            />
          </div>

          {searchInProgress ? (
            <div className={css.searchInProgress}>
              <IconSpinner />
            </div>
          ) : null}

          <div className={css.introContent}>
            {isArrayLength(filteredArticles)
              ? filteredArticles.map(a => (
                  <div
                    key={a?.id}
                    className={css.introInnerBlock}
                    role="button"
                    onClick={() =>
                      navigateToDetailPage({
                        title: createSlug(a?.title || draftSlug),
                        id: String(a?.id),
                        slug: currentSlug,
                      })
                    }
                  >
                    <h3>{a?.title}</h3>
                    {isArrayLength(a?.description)
                      ? truncateText(a?.description[0]?.children[0]?.text)
                      : ''}
                  </div>
                ))
              : null}
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

SupportListPageComponent.defaultProps = {
  staticContext: {},
};

SupportListPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  marketplaceName: string.isRequired,
  isKeywordSearch: bool.isRequired,
  staticContext: object,
  intl: intlShape.isRequired,
  routeConfiguration: arrayOf(propTypes.route).isRequired,
  history: shape({
    push: func.isRequired,
  }).isRequired,
  params: shape({
    type: string,
  }).isRequired,
};

const EnhancedSupportListPage = props => {
  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();
  const history = useHistory();
  const intl = useIntl();
  const params = useParams();

  return (
    <SupportListPageComponent
      routeConfiguration={routeConfiguration}
      marketplaceName={config.marketplaceName}
      isKeywordSearch={isMainSearchTypeKeywords(config)}
      history={history}
      params={params}
      intl={intl}
      {...props}
    />
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const SupportListPage = compose(connect(mapStateToProps))(EnhancedSupportListPage);

export default SupportListPage;
